// packages/app/src/scaffolder/UserTeamPicker.tsx

import { useEffect, useState } from 'react';
import { useApi, identityApiRef, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';

import { parseEksTeam } from '../utils/eksTeam';

interface Team {
  team_key:    string;
  team_name:   string;
  description: string;
}

/**
 * Extracts the team short name from a lead group ref.
 * Input:  "group:default/eks-team-integ-lead"
 * Output: "integ"
 * Returns null for non-lead groups (dev, all, etc.)
 */
function extractLeadTeam(ref: string): string | null {
  const parsed = parseEksTeam(ref);
  return parsed && parsed.role === 'lead' ? parsed.teamKey : null;
}

/**
 * Returns true if user is a platform member (plat-lead or plat-dev).
 * Platform members see ALL teams in the dropdown.
 */
function isPlatformMember(refs: string[]): boolean {
  return refs.some(ref => {
    const parsed = parseEksTeam(ref);
    return parsed !== null && parsed.isPlatform && (parsed.role === 'lead' || parsed.role === 'dev');
  });
}

/**
 * UserTeamPickerComponent
 *
 * Fetches teams dynamically from the backend DB (scaffolder.backstage_teams)
 * and filters based on the user's role:
 *
 *   eks-team-plat-lead / eks-team-plat-dev → sees ALL teams
 *   eks-team-{team}-lead                   → sees only their lead teams
 */
export const UserTeamPickerComponent = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const identityApi = useApi(identityApiRef);
  const fetchApi    = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [teams, setTeams]     = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [isPlat, setIsPlat]   = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Step 1 — get user's group refs
        const identity = await identityApi.getBackstageIdentity();
        const refs      = identity.ownershipEntityRefs ?? [];

        // Step 2 — check if platform member (sees all teams)
        const platMember = isPlatformMember(refs);
        setIsPlat(platMember);

        // Step 3 — fetch all teams from backend DB. discoveryApi resolves the
        // real backend URL and identityApi's token is attached explicitly —
        // see backendFetch in DashboardPage.tsx for why a plain relative
        // fetchApi.fetch('/api/teams') stops authenticating once frontend
        // and backend are on different origins.
        const baseUrl = await discoveryApi.getBaseUrl('teams');
        const { token } = await identityApi.getCredentials();
        const response = await fetchApi.fetch(baseUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch teams: ${response.statusText}`);
        }
        const data: { teams: Team[] } = await response.json();

        let filtered: Team[];

        if (platMember) {
          // Platform members see ALL teams
          filtered = data.teams;
        } else {
          // Regular leads see only their teams
          const leadKeys = refs
            .map(extractLeadTeam)
            .filter((t): t is string => t !== null);
          const leadKeySet = new Set(leadKeys);
          filtered = data.teams.filter(t => leadKeySet.has(t.team_key));
        }

        setTeams(filtered);

        // Auto-select if only one team
        if (filtered.length === 1 && !formData) {
          onChange(filtered[0].team_key);
        }
      } catch {
        setError('Could not load teams. Please refresh.');
      } finally {
        setLoading(false);
      }
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasError = (rawErrors?.length ?? 0) > 0;
  let helperText = 'Only teams where you hold a lead role are shown.';
  if (error) {
    helperText = error;
  } else if (hasError) {
    helperText = rawErrors![0];
  } else if (loading) {
    helperText = 'Loading your teams…';
  } else if (teams.length === 0) {
    helperText = 'No teams found. Ask a platform lead to create your team first.';
  } else if (isPlat) {
    helperText = 'Showing all teams (platform access).';
  }

  return (
    <FormControl required={required} error={hasError} fullWidth>
      <InputLabel shrink>{schema?.title ?? 'Owner'}</InputLabel>

      {loading ? (
        <CircularProgress size={24} style={{ marginTop: 16 }} />
      ) : (
        <Select
          value={formData ?? ''}
          onChange={e => onChange(e.target.value as string)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            <em>Select a team…</em>
          </MenuItem>

          {teams.map(team => (
            <MenuItem key={team.team_key} value={team.team_key}>
              {team.team_name}
            </MenuItem>
          ))}
        </Select>
      )}

      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
